/**
 * Online/offline awareness (§4.4, Appendix A `offline` / `stale_data`). The banner component lives in
 * `components/OfflineBanner.tsx`; this module is the hook + the "unconfirmed result" classification every
 * mutation screen uses: on a network failure DURING a submission the participant must be told
 * "We couldn't confirm the result — check status before trying again" and offered a status check that reuses
 * the same Idempotency-Key — never a blind retry.
 */
import { useSyncExternalStore } from 'react';
import { ApiError, NetworkError } from './api/types';

const subscribe = (cb: () => void) => {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
};
const getOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, getOnline, () => true);
}

/**
 * Message keys in the app bundle. `status.<condition>` mirrors `PARTICIPANT_STATUS_COPY[condition].text`
 * from `@windrop/contracts` (parity test in `contracts-parity.test.ts`); the bundle keeps the contracts
 * runtime out of the initial chunk and gives localization a home (§15.4).
 */
export const OFFLINE_KEY = 'status.offline';
export const STALE_KEY = 'status.stale_data';
/** §4.4 network-loss-during-submission sentence. */
export const UNCONFIRMED_RESULT_KEY = 'status.unconfirmed';

export type SubmitFailureKind = 'unconfirmed' | 'rejected' | 'unknown';

/**
 * Classifies a failed mutation. `unconfirmed` = the request may have reached the server (network error,
 * timeout, 5xx, 429): show the unconfirmed-result copy and a status check with the same key. `rejected` = the
 * server answered with a 4xx problem: show its detail; the participant may change input and resubmit.
 */
export function classifySubmitFailure(error: unknown): SubmitFailureKind {
  if (error instanceof NetworkError) return 'unconfirmed';
  if (error instanceof ApiError)
    return error.status >= 500 || error.status === 429 || error.status === 408 ? 'unconfirmed' : 'rejected';
  if (error instanceof DOMException && error.name === 'AbortError') return 'unconfirmed';
  return 'unknown';
}
